import React, { useState, useEffect } from 'react';
import { useBooking } from './context/BookingContext';
import { Property } from './types';
import PricingInfo from './components/PricingInfo';
import SEO from './components/SEO';
import StructuredData from './components/StructuredData';
import BedroomsSection from './components/BedroomsSection';
import AmenitiesSection from './components/AmenitiesSection';
import { LocationSection } from './components/LocationSection';
import { useTranslation } from 'react-i18next';
import { FaEnvelope, FaWhatsapp } from 'react-icons/fa';
import { LanguageSwitcher } from './components/LanguageSwitcher';
import MobileBottomNav from './components/MobileBottomNav';

// Mock property data
const mockProperty: Property = {
  id: '1',
  name: 'Villa Pinos Alhaurín',
  location: 'Pinos de Alhaurín, Málaga',
  postalCode: '29130',
  price: 45,
  numberOfBedrooms: 5,
  numberOfBathrooms: 4,
  numberOfBeds: 10,
  maxOccupancy: 10,
  checkInTime: '14:00-20:00',
  checkOutTime: '11:00',
  description: 'about.description',
  highlights: ['about.highlights.items.0', 'about.highlights.items.1', 'about.highlights.items.2'],
  amenities: ['about.amenities.items.pool', 'about.amenities.items.wifi', 'about.amenities.items.kitchen', 'about.amenities.items.parking', 'about.amenities.items.ac', 'about.amenities.items.tv', 'about.amenities.items.dining', 'about.amenities.items.bbq', 'about.amenities.items.charger', 'about.amenities.items.lighting', 'about.amenities.items.washing', 'about.amenities.items.smoking'],
  images: [
    '/images/pool/main.jpg', '/images/pool/dawn.jpg', '/images/pool/nightly.jpg', '/images/pool/garden.jpg', '/images/pool/pool-evening.jpg',
    '/images/interior/living-room.jpg', '/images/interior/tvlivingroom.jpg', '/images/interior/master-bedroom.jpg', '/images/interior/kitchen.jpg', '/images/interior/kitchensupplies.jpg',
    '/images/interior/guestroom2.jpg', '/images/interior/bathroom gestroom45.jpg', '/images/interior/bathroom guestroom2.jpg', '/images/interior/guestrooms-hallway.jpg',
    '/images/exterior/backside_terrace1.jpg', '/images/exterior/backside_terrace2.jpg'
  ],
  host: { id: 'host1', name: 'Owner', avatar: '', isSuperhost: true, yearsHosting: 5, responseRate: 100, responseTime: 'within an hour' },
  reviews: [],
  availability: []
};


const App: React.FC = () => {
  const { t } = useTranslation();
  const { setCheckIn, setCheckOut } = useBooking();
  const [showMore, setShowMore] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);

  const [bookingData, setBookingData] = useState({ checkIn: '', checkOut: '', guests: '5' });
  const [contactData, setContactData] = useState({
    name: '',
    email: '',
    message: '',
    phone: '',
    guestList: '',
    mainContactFirstName: '',
    mainContactLastName: '',
    mainContactAge: '',
    mainContactStreet: '',
    mainContactHouseNumber: '',
    mainContactAdditionalNumber: '',
    mainContactPostalCode: '',
    mainContactCity: '',
    mainContactCountry: ''
  });
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeSection, setActiveSection] = useState('home');
  const [availabilityResult, setAvailabilityResult] = useState<any>(null);





  const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:3001';

  // Smooth scroll to section
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Keyboard navigation for image modal
  useEffect(() => {
    if (!showImageModal) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        setSelectedImage(prev => prev > 0 ? prev - 1 : mockProperty.images.length - 1);
      } else if (e.key === 'ArrowRight') {
        setSelectedImage(prev => prev < mockProperty.images.length - 1 ? prev + 1 : 0);
      } else if (e.key === 'Escape') {
        setShowImageModal(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showImageModal, mockProperty.images.length]);

  // Scroll tracking for progress indicator and active section
  useEffect(() => {
    const handleScroll = () => {
      // Calculate scroll progress
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY;
      const scrollPercentage = (scrollTop / (documentHeight - windowHeight)) * 100;
      setScrollProgress(Math.min(scrollPercentage, 100));

      // Determine active section
      const sections = ['home', 'about', 'amenities', 'gallery', 'location', 'pricing', 'contact'];
      let currentSection = 'home';

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          // Section is active if its top is in the upper half of viewport
          if (rect.top <= windowHeight / 2 && rect.bottom >= windowHeight / 2) {
            currentSection = section;
            break;
          }
        }
      }

      setActiveSection(currentSection);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial call

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleImageClick = (index: number) => {
    setSelectedImage(index);
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
  };





  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contactData.name || !contactData.email || !contactData.message) {
      alert(t('messages.errors.fillAllFields'));
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...contactData, property: mockProperty.name })
      });

      const data = await response.json();

      if (data.success) {
        setAvailabilityResult({
          available: true,
          message: data.message || t('messages.success.messageSent')
        });
        setContactData({
          name: '',
          email: '',
          message: '',
          phone: '',
          guestList: '',
          mainContactFirstName: '',
          mainContactLastName: '',
          mainContactAge: '',
          mainContactStreet: '',
          mainContactHouseNumber: '',
          mainContactAdditionalNumber: '',
          mainContactPostalCode: '',
          mainContactCity: '',
          mainContactCountry: ''
        });
        setShowContactModal(false);
      } else {
        setAvailabilityResult({ available: false, message: `${t('messages.errors.failedToSendMessage')} ${data.error}` });
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setAvailabilityResult({ available: false, message: t('messages.errors.failedToSendMessage') });
    }
  };

  const handleBookingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate basic contact fields (email and phone are required)
    if (!contactData.email || !contactData.phone) {
      alert(t('messages.errors.fillAllFields'));
      return;
    }

    // Validate main contact has complete data
    if (!contactData.mainContactFirstName || !contactData.mainContactLastName || !contactData.mainContactAge) {
      alert(t('messages.errors.fillAllFields'));
      return;
    }

    // Validate guest list - check if it's valid JSON and has data
    let guestListData;
    try {
      guestListData = contactData.guestList ? JSON.parse(contactData.guestList) : [];
    } catch (e) {
      alert(t('messages.errors.guestNamesRequired'));
      return;
    }

    // Check if we have guest data
    if (!guestListData || guestListData.length === 0) {
      alert(t('messages.errors.guestNamesRequired'));
      return;
    }

    // Validate that all guests have complete data
    for (let i = 0; i < guestListData.length; i++) {
      const guest = guestListData[i];
      if (!guest || !guest.firstName || !guest.lastName || !guest.age) {
        alert(`${t('messages.errors.guestNamesRequired')} (Guest ${i + 1})`);
        return;
      }
    }

    // Check if dates are selected and valid
    if (!bookingData.checkIn || !bookingData.checkOut) {
      alert(t('messages.errors.selectBothDates'));
      return;
    }

    // Check if availability has been checked and is available
    if (!availabilityResult || !availabilityResult.available) {
      // Try to check availability first
      await handleCheckAvailability();

      // Re-check after availability check
      if (!availabilityResult || !availabilityResult.available) {
        alert(t('messages.errors.selectBothDates'));
        return;
      }
    }

    try {
      // Create complete guest list including main contact
      const completeGuestList = [
        {
          firstName: contactData.mainContactFirstName,
          lastName: contactData.mainContactLastName,
          age: contactData.mainContactAge,
          isMainContact: true
        },
        ...guestListData
      ];

      const bookingRequest = {
        property: mockProperty.name,
        checkIn: bookingData.checkIn,
        checkOut: bookingData.checkOut,
        guests: bookingData.guests,
        total: availabilityResult.grandTotal,
        email: contactData.email,
        phone: contactData.phone,
        message: contactData.message,
        isBookingRequest: true,
        bookingDetails: {
          checkIn: bookingData.checkIn,
          checkOut: bookingData.checkOut,
          guests: bookingData.guests,
          totalPrice: availabilityResult.grandTotal,
          breakdown: availabilityResult.breakdown,
          seasonTotal: availabilityResult.seasonTotal,
          cleaningFee: availabilityResult.cleaningFee,
          serviceFee: availabilityResult.serviceFee,
          grandTotal: availabilityResult.grandTotal
        },
        mainContactFirstName: contactData.mainContactFirstName,
        mainContactLastName: contactData.mainContactLastName,
        mainContactAge: contactData.mainContactAge,
        mainContactStreet: contactData.mainContactStreet,
        mainContactHouseNumber: contactData.mainContactHouseNumber,
        mainContactAdditionalNumber: contactData.mainContactAdditionalNumber,
        mainContactPostalCode: contactData.mainContactPostalCode,
        mainContactCity: contactData.mainContactCity,
        mainContactCountry: contactData.mainContactCountry,
        guestList: JSON.stringify(completeGuestList)
      };

      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bookingRequest)
      });

      const data = await response.json();

      if (data.success) {
        setAvailabilityResult({
          available: true,
          message: data.message || t('messages.success.bookingSent')
        });
        setContactData({
          name: '',
          email: '',
          message: '',
          phone: '',
          guestList: '',
          mainContactFirstName: '',
          mainContactLastName: '',
          mainContactAge: '',
          mainContactStreet: '',
          mainContactHouseNumber: '',
          mainContactAdditionalNumber: '',
          mainContactPostalCode: '',
          mainContactCity: '',
          mainContactCountry: ''
        });
        setShowBookingModal(false);
        // Reset booking data
        setBookingData({ checkIn: '', checkOut: '', guests: '5' });
        setCheckIn(undefined as any);
        setCheckOut(undefined as any);
      } else {
        setAvailabilityResult({ available: false, message: `${t('messages.errors.failedToSendBooking')} ${data.error}` });
      }
    } catch (error) {
      console.error('Error sending booking request:', error);
      setAvailabilityResult({ available: false, message: t('messages.errors.failedToSendBooking') });
    }
  };

  const handleCheckAvailability = async () => {
    if (!bookingData.checkIn || !bookingData.checkOut) {
      setAvailabilityResult({ available: false, message: t('messages.errors.selectBothDates') });
      return;
    }

    const checkInDate = new Date(bookingData.checkIn);
    const checkOutDate = new Date(bookingData.checkOut);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (checkInDate < today) {
      setAvailabilityResult({ available: false, message: t('messages.errors.dateInPast') });
      return;
    }

    if (checkOutDate <= checkInDate) {
      setAvailabilityResult({ available: false, message: t('messages.errors.checkOutAfterCheckIn') });
      return;
    }

    const nights = Math.ceil((checkOutDate.getTime() - checkInDate.getTime()) / (1000 * 60 * 60 * 24));
    if (nights > 21) {
      setAvailabilityResult({ available: false, message: t('messages.errors.maxStay') });
      return;
    }

    if (nights < 5) {
      setAvailabilityResult({ available: false, message: t('messages.errors.minStay') });
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/availability`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          checkIn: bookingData.checkIn,
          checkOut: bookingData.checkOut,
          guests: parseInt(bookingData.guests)
        })
      });

      const data = await response.json();

      if (data.available) {
        setAvailabilityResult({
          available: true,
          message: `${t('messages.success.available')} €${data.price} total for ${data.nights} nights`,
          price: data.price,
          nights: data.nights,
          breakdown: data.breakdown,
          seasonTotal: data.price,
          cleaningFee: 80,
          serviceFee: 120,
          grandTotal: data.price + 80 + 120
        });
      } else {
        const errorMsg = data.error || data.message || t('booking.notAvailable');
        setAvailabilityResult({ available: false, message: `${errorMsg}` });
      }
    } catch (error) {
      console.error('Error checking availability:', error);
      setAvailabilityResult({ available: false, message: t('messages.errors.failedToCheckAvailability') });
    }
  };







  return (
    <div className="min-h-screen bg-warm-white pb-20 md:pb-0">
      <MobileBottomNav />
      <SEO />
      <StructuredData property={mockProperty} />

      {/* Navigation Bar */}
      <nav className="navbar">
        {/* Scroll Progress Indicator */}
        <div
          className="scroll-progress-bar"
          style={{ width: `${scrollProgress}%` }}
        />
        <div className="container">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img
                src="/images/logo/villa_pinos_alhaurin_logo.png"
                alt="Villa Pinos Alhaurín Logo"
                className="h-10 w-auto object-contain"
              />
              <span className="sr-only">Villa Pinos Alhaurín</span>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection('home')} className={`nav-link ${activeSection === 'home' ? 'nav-link-active' : ''}`}>{t('nav.home')}</button>
              <button onClick={() => scrollToSection('about')} className={`nav-link ${activeSection === 'about' ? 'nav-link-active' : ''}`}>{t('nav.about')}</button>
              <button onClick={() => scrollToSection('amenities')} className={`nav-link ${activeSection === 'amenities' ? 'nav-link-active' : ''}`}>{t('nav.amenities')}</button>
              <button onClick={() => scrollToSection('location')} className={`nav-link ${activeSection === 'location' ? 'nav-link-active' : ''}`}>{t('nav.location')}</button>
              <button onClick={() => scrollToSection('gallery')} className={`nav-link ${activeSection === 'gallery' ? 'nav-link-active' : ''}`}>{t('gallery.title')}</button>
              <button onClick={() => scrollToSection('pricing')} className={`nav-link ${activeSection === 'pricing' ? 'nav-link-active' : ''}`}>{t('nav.pricing')}</button>
              <a href="#availability" className="btn-book flex items-center gap-2" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }}>
                {t('nav.check_availability')}
              </a>
              <button onClick={() => scrollToSection('contact')} className={`nav-link ${activeSection === 'contact' ? 'nav-link-active' : ''}`}>{t('nav.contact')}</button>
              <LanguageSwitcher />
            </div>
            <div className="md:hidden flex items-center gap-3">
              <LanguageSwitcher />
              <a href="#availability" className="btn-book py-2 px-4 text-sm" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }}>
                {t('nav.check_availability')}
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Spacer for fixed nav */}
      <div className="h-16"></div>

      {/* Hero Section */}
      <section id="home" className="relative h-[70vh] md:h-[85vh] flex items-end md:items-center justify-start bg-moorish overflow-hidden">
        {/* Background Image with Parallax Effect */}
        <div
          className="absolute inset-0 hero-image-container"
          style={{
            backgroundImage: `url('/images/pool/main.jpg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            transform: 'scale(1.1)',
            transition: 'transform 0.3s ease-out'
          }}
        />

        {/* Dark Gradient Overlay for Text Contrast */}
        <div className="absolute inset-0 hero-gradient" />

        {/* Asymmetric Content Container - Left Aligned */}
        <div className="relative z-10 container pb-16 md:pb-24">
          <div className="max-w-3xl hero-content-animate">
            <h1 className="heading-main text-white mb-6 drop-shadow-lg text-5xl md:text-6xl lg:text-7xl">
              {t('hero.title')}
            </h1>
            <p className="text-display text-white/95 mb-8 font-light tracking-wide drop-shadow-md">
              {t('hero.tagline')}
            </p>
            <div className="divider-geometric !mx-0 !bg-white/80" style={{ width: '120px' }}></div>
            <p className="text-accent text-white/85 mb-10 drop-shadow-md max-w-xl">
              {t('about.guests')}
            </p>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 px-6 bg-warm-white">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16 items-start">
            <div className="section-card card-hover-float bg-moorish lg:col-span-3">
              <div className="flex items-center gap-6 mb-8">
                <img
                  src="/images/logo/villa_pinos_alhaurin_logo.png"
                  alt="Villa Pinos Alhaurín Logo"
                  className="h-64 w-auto object-contain"
                />
                <h2 className="heading-main">{t('about.title')}</h2>
              </div>
              <div className="divider-geometric"></div>
              <p className={`text-body mb-8 ${showMore ? '' : 'line-clamp-3'}`}>
                {t('about.description')}
              </p>
              <button
                onClick={() => setShowMore(!showMore)}
                className="link-clean font-medium text-lg"
              >
                {showMore ? t('common.showLess') : t('common.showMore')} →
              </button>

              {/* Property Facts */}
              <div className="mt-8 bg-warm-cream p-6 border border-stone/20">
                <h3 className="heading-sub mb-3">{t('about.facts.title')}</h3>
                <div className="grid grid-cols-2 gap-3 text-gray-700">
                  <div className="font-semibold">{t('about.facts.floorSize')}</div>
                  <div className="font-semibold">{t('about.facts.rooms')}</div>
                  <div className="font-semibold">{t('about.facts.bedrooms')}</div>
                  <div className="font-semibold">{t('about.facts.bathrooms')}</div>
                  <div className="font-semibold col-span-2">{t('about.facts.capacity')}</div>
                </div>
              </div>


            </div>
          </div>
        </div>
      </section>

      {/* Bedrooms Section */}
      <BedroomsSection />

      {/* Amenities Section - NEW */}
      <AmenitiesSection />

      {/* Gallery Section */}
      <section id="gallery" className="py-24 px-6 bg-warm-white">
        <div className="container">
          <div className="section-card">
            <h2 className="heading-main text-center mb-10">{t('gallery.title')}</h2>
            <div className="divider-geometric"></div>
            <div className="gallery-grid">
              <div className="gallery-item col-span-2">
                <img
                  src={mockProperty.images[0]}
                  alt="Main"
                  className="w-full h-full object-cover image-hover-lift cursor-pointer"
                  onClick={() => handleImageClick(0)}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = '/images/pool/main.jpg';
                  }}
                />
              </div>
            </div>
            <div className="gallery-grid grid-cols-5 mt-2">
              {mockProperty.images.slice(1, 6).map((img, index) => (
                <div key={index} className="gallery-item">
                  <img
                    src={img}
                    alt={`Property ${index + 2}`}
                    className="w-full h-full object-cover image-hover-lift cursor-pointer"
                    onClick={() => handleImageClick(index + 1)}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/images/pool/main.jpg';
                    }}
                  />
                </div>
              ))}
            </div>
            <button
              className="btn-book btn-ripple w-full mt-6"
              onClick={() => handleImageClick(0)}
            >
              {t('gallery.showAll')}
            </button>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <LocationSection />

      {/* Pricing Section */}
      <section id="pricing" className="py-16 px-6 bg-warm-white">
        <div className="container">
          <div className="section-card">
            <h2 className="heading-main text-center mb-10">{t('pricing.title')}</h2>
            <div className="divider-geometric"></div>
            <div className="bg-warm-cream p-6 border border-stone/20">
              <PricingInfo basePrice={45} />
            </div>
          </div>
        </div>
      </section>


      {/* Book Direct Benefits Section */}
      <section id="direct-benefits" className="py-16 px-6 bg-warm-white">
        <div className="container">
          <div className="section-card">
            <h2 className="heading-main text-center mb-10">{t('directBooking.title')}</h2>
            <div className="divider-geometric"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="feature-item flex-col items-start">
                <div className="text-4xl mb-3">💰</div>
                <h3 className="font-bold mb-2 text-gray-800">{t('directBooking.betterPrice.title')}</h3>
                <p className="text-gray-600 font-medium">{t('directBooking.betterPrice.text')}</p>
              </div>
              <div className="feature-item flex-col items-start">
                <div className="text-4xl mb-3">🤝</div>
                <h3 className="font-bold mb-2 text-gray-800">{t('directBooking.communication.title')}</h3>
                <p className="text-gray-600 font-medium">{t('directBooking.communication.text')}</p>
              </div>
              <div className="feature-item flex-col items-start">
                <div className="text-4xl mb-3">⚡</div>
                <h3 className="font-bold mb-2 text-gray-800">{t('directBooking.flexibility.title')}</h3>
                <p className="text-gray-600 font-medium">{t('directBooking.flexibility.text')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Languages Section */}
      <section id="languages" className="py-16 px-6 bg-warm-white">
        <div className="container">
          <div className="section-card">
            <h2 className="heading-main text-center mb-6">🌍 {t('multilingual.title')}</h2>
            <p className="text-center text-body mb-10">{t('multilingual.subtitle')}</p>
            <div className="divider-geometric"></div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-10">
              <div className="feature-item flex-col items-center text-center">
                <div className="text-4xl mb-2">🇬🇧</div>
                <div className="font-bold text-gray-800">English</div>
              </div>
              <div className="feature-item flex-col items-center text-center">
                <div className="text-4xl mb-2">🇫🇷</div>
                <div className="font-bold text-gray-800">French</div>
              </div>
              <div className="feature-item flex-col items-center text-center">
                <div className="text-4xl mb-2">🇳🇱</div>
                <div className="font-bold text-gray-800">Dutch</div>
              </div>
              <div className="feature-item flex-col items-center text-center">
                <div className="text-4xl mb-2">🇪🇸</div>
                <div className="font-bold text-gray-800">Spanish</div>
              </div>
              <div className="feature-item flex-col items-center text-center">
                <div className="text-4xl mb-2">🇩🇪</div>
                <div className="font-bold text-gray-800">German</div>
              </div>
              <div className="feature-item flex-col items-center text-center">
                <div className="text-4xl mb-2">🇹🇭</div>
                <div className="font-bold text-gray-800">Thai</div>
              </div>
            </div>

            <div className="bg-warm-cream p-6 border border-stone/20 max-w-3xl mx-auto">
              <h3 className="heading-sub mb-3 text-center">{t('multilingual.whyItMatters.title')}</h3>
              <p className="text-body text-center">
                {t('multilingual.whyItMatters.text')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 px-6 bg-warm-white">
        <div className="max-w-2xl mx-auto">
          <div className="section-card">
            <h2 className="heading-main text-center mb-8">{t('contact.title')}</h2>
            <div className="divider-geometric"></div>
            <div className="bg-warm-cream p-6 border border-stone/20">
              <p className="text-center text-body mb-6">
                {t('contact.description')}
              </p>
              <div className="bg-warm-cream p-4 mb-6 border border-stone/20">
                <p className="text-gray-800 font-bold text-center">
                  <strong>{t('contact.directContact.title')}:</strong> {t('contact.directContact.text')}
                </p>
              </div>
              <div className="space-y-4">
                <div className="bg-white p-4 rounded-architectural border border-stone/20 text-center">
                  <p className="text-sm text-gray-600 mb-1">{t('contact.phone')}</p>
                  <p className="text-xl font-bold text-terracotta">+32 475 490 137</p>
                </div>
                <button
                  onClick={() => setShowContactModal(true)}
                  className="btn-book w-full flex items-center justify-center gap-3"
                >
                  <FaEnvelope className="text-xl" /> {t('contact.sendEmail')}
                </button>
                <a
                  href="https://wa.me/32475490137"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full bg-green-600 text-white font-bold py-4 rounded-architectural hover:bg-green-700 transition-all flex items-center justify-center gap-3"
                >
                  <FaWhatsapp className="text-xl" /> {t('contact.whatsapp')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="flex flex-col items-center text-center">
            <img
              src="/images/logo/villa_pinos_alhaurin_logo.png"
              alt="Villa Pinos Alhaurín Logo"
              className="h-16 w-auto object-contain mb-4"
            />
            <p className="text-white/90 mb-2">
              <strong>- {t('common.rights')}</strong>
            </p>
            <p className="text-white/70 text-sm">{t('common.crafted')}</p>
          </div>
        </div>
      </footer>

      {/* Image Modal */}
      {showImageModal && (
        <div className="modal-overlay flex flex-col" onClick={closeImageModal}>
          <div className="flex items-center justify-between p-4 bg-black/70">
            <div className="text-white font-semibold">
              {selectedImage + 1} / {mockProperty.images.length}
            </div>
            <button
              onClick={closeImageModal}
              className="text-white text-3xl hover:text-gray-300 font-bold"
            >
              ×
            </button>
          </div>

          <div className="flex-1 flex items-center justify-center relative" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setSelectedImage(prev => prev > 0 ? prev - 1 : mockProperty.images.length - 1)}
              className="absolute left-4 text-white text-4xl hover:text-gray-300 p-4 bg-black/50 rounded-architectural"
            >
              ←
            </button>

            <img
              src={mockProperty.images[selectedImage]}
              alt={`Property ${selectedImage + 1}`}
              className="max-w-[90%] max-h-[70vh] object-contain rounded-architectural"
              onError={(e) => {
                (e.target as HTMLImageElement).src = '/images/pool/main.jpg';
              }}
            />

            <button
              onClick={() => setSelectedImage(prev => prev < mockProperty.images.length - 1 ? prev + 1 : 0)}
              className="absolute right-4 text-white text-4xl hover:text-gray-300 p-4 bg-black/50 rounded-architectural"
            >
              →
            </button>
          </div>

          <div className="p-4 bg-black/70 overflow-x-auto">
            <div className="flex gap-2 justify-center">
              {mockProperty.images.map((img, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`flex-shrink-0 w-16 h-16 border-2 rounded overflow-hidden ${index === selectedImage
                    ? 'border-white opacity-100'
                    : 'border-gray-600 opacity-60 hover:opacity-100'
                    }`}
                >
                  <img
                    src={img}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/images/pool/main.jpg';
                    }}
                  />
                </button>
              ))}
            </div>
          </div>

          <div className="text-center text-white text-sm p-2 bg-black/70">
            Use ← → arrow keys or click thumbnails to navigate
          </div>
        </div>
      )}

      {/* Contact Modal */}
      {showContactModal && (
        <div className="modal-overlay flex items-center justify-center p-4" onClick={() => setShowContactModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="heading-sub">{t('contactModal.title')}</h3>
              <button onClick={() => setShowContactModal(false)} className="text-gray-500 hover:text-gray-700 text-2xl font-bold">×</button>
            </div>
            <form onSubmit={handleContactSubmit} className="space-y-4">
              <div>
                <label className="label">{t('booking.guestList')}</label>
                <input
                  type="text"
                  className="input-clean"
                  value={contactData.name}
                  onChange={(e) => setContactData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder={t('booking.guestList')}
                  title={t('booking.guestList')}
                  required
                />
              </div>
              <div>
                <label className="label">{t('booking.email')}</label>
                <input
                  type="email"
                  className="input-clean"
                  value={contactData.email}
                  onChange={(e) => setContactData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="your.email@example.com"
                  title={t('booking.email')}
                  required
                />
              </div>
              <div>
                <label className="label">{t('booking.message')}</label>
                <textarea
                  className="input-clean h-32"
                  value={contactData.message}
                  onChange={(e) => setContactData(prev => ({ ...prev, message: e.target.value }))}
                  placeholder={t('booking.message')}
                  title={t('booking.message')}
                  required
                />
              </div>
              <button
                type="submit"
                className="btn-book w-full"
              >
                {t('contact.sendEmail')}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Booking Modal */}
      {showBookingModal && (
        <div className="modal-overlay flex items-center justify-center p-4" onClick={() => setShowBookingModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="heading-sub">{t('booking.requestForm')}</h3>
              <button onClick={() => setShowBookingModal(false)} className="text-gray-500 hover:text-gray-700 text-2xl font-bold">×</button>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-architectural p-4 mb-6">
              <p className="text-sm text-yellow-800 font-semibold">
                ⚠️ {t('booking.formNote')}
              </p>
            </div>

            <div className="bg-blue-50 rounded-architectural p-4 mb-6">
              <p className="font-bold mb-2">{t('booking.process')}:</p>
              <ol className="list-decimal list-inside space-y-1 text-gray-700">
                {[0, 1, 2, 3].map((index) => (
                  <li key={index}>{t(`booking.processSteps.${index}`)}</li>
                ))}
              </ol>
            </div>

            <form onSubmit={handleBookingSubmit} className="space-y-4">
              {/* Main Contact Information */}
              <div>
                <label className="label font-bold">{t('booking.mainContact')}</label>
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-2">
                    <input
                      type="text"
                      className="input-clean"
                      placeholder={t('booking.firstName')}
                      title={t('booking.firstName')}
                      value={contactData.mainContactFirstName}
                      onChange={(e) => {
                        setContactData(prev => ({ ...prev, mainContactFirstName: e.target.value }));
                      }}
                    />
                    <input
                      type="text"
                      className="input-clean"
                      placeholder={t('booking.lastName')}
                      title={t('booking.lastName')}
                      value={contactData.mainContactLastName}
                      onChange={(e) => {
                        setContactData(prev => ({ ...prev, mainContactLastName: e.target.value }));
                      }}
                    />
                    <input
                      type="number"
                      className="input-clean"
                      placeholder={t('booking.age')}
                      title={t('booking.age')}
                      min="0"
                      max="120"
                      value={contactData.mainContactAge}
                      onChange={(e) => {
                        setContactData(prev => ({ ...prev, mainContactAge: e.target.value }));
                      }}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="email"
                      className="input-clean"
                      value={contactData.email}
                      onChange={(e) => setContactData(prev => ({ ...prev, email: e.target.value }))}
                      placeholder={t('booking.email')}
                      title={t('booking.email')}
                      required
                    />
                    <input
                      type="tel"
                      className="input-clean"
                      value={contactData.phone}
                      onChange={(e) => setContactData(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder={t('booking.phone')}
                      title={t('booking.phone')}
                      required
                    />
                  </div>
                  {/* Address Fields */}
                  <div className="space-y-2">
                    <input
                      type="text"
                      className="input-clean"
                      placeholder={t('booking.street')}
                      title={t('booking.street')}
                      value={contactData.mainContactStreet}
                      onChange={(e) => {
                        setContactData(prev => ({ ...prev, mainContactStreet: e.target.value }));
                      }}
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="text"
                        className="input-clean"
                        placeholder={t('booking.houseNumber')}
                        title={t('booking.houseNumber')}
                        value={contactData.mainContactHouseNumber}
                        onChange={(e) => {
                          setContactData(prev => ({ ...prev, mainContactHouseNumber: e.target.value }));
                        }}
                      />
                      <input
                        type="text"
                        className="input-clean"
                        placeholder={t('booking.additionalNumber')}
                        title={t('booking.additionalNumber')}
                        value={contactData.mainContactAdditionalNumber}
                        onChange={(e) => {
                          setContactData(prev => ({ ...prev, mainContactAdditionalNumber: e.target.value }));
                        }}
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <input
                        type="text"
                        className="input-clean"
                        placeholder={t('booking.postalCode')}
                        title={t('booking.postalCode')}
                        value={contactData.mainContactPostalCode}
                        onChange={(e) => {
                          setContactData(prev => ({ ...prev, mainContactPostalCode: e.target.value }));
                        }}
                      />
                      <input
                        type="text"
                        className="input-clean"
                        placeholder={t('booking.city')}
                        title={t('booking.city')}
                        value={contactData.mainContactCity}
                        onChange={(e) => {
                          setContactData(prev => ({ ...prev, mainContactCity: e.target.value }));
                        }}
                      />
                      <input
                        type="text"
                        className="input-clean"
                        placeholder={t('booking.country')}
                        title={t('booking.country')}
                        value={contactData.mainContactCountry}
                        onChange={(e) => {
                          setContactData(prev => ({ ...prev, mainContactCountry: e.target.value }));
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Guests */}
              <div>
                <label className="label font-bold">{t('booking.additionalGuests')} (4 guests)</label>
                <div className="space-y-2">
                  {[0, 1, 2, 3].map((index) => {
                    const guests = contactData.guestList ? JSON.parse(contactData.guestList) : [];
                    const guest = guests[index] || {};
                    return (
                      <div key={index} className="grid grid-cols-3 gap-2">
                        <input
                          type="text"
                          className="input-clean"
                          placeholder={t('booking.firstName')}
                          title={t('booking.firstName')}
                          value={guest.firstName || ''}
                          onChange={(e) => {
                            const newGuests = contactData.guestList ? JSON.parse(contactData.guestList) : [];
                            if (!newGuests[index]) newGuests[index] = {};
                            newGuests[index].firstName = e.target.value;
                            setContactData(prev => ({ ...prev, guestList: JSON.stringify(newGuests) }));
                          }}
                        />
                        <input
                          type="text"
                          className="input-clean"
                          placeholder={t('booking.lastName')}
                          title={t('booking.lastName')}
                          value={guest.lastName || ''}
                          onChange={(e) => {
                            const newGuests = contactData.guestList ? JSON.parse(contactData.guestList) : [];
                            if (!newGuests[index]) newGuests[index] = {};
                            newGuests[index].lastName = e.target.value;
                            setContactData(prev => ({ ...prev, guestList: JSON.stringify(newGuests) }));
                          }}
                        />
                        <input
                          type="number"
                          className="input-clean"
                          placeholder={t('booking.age')}
                          title={t('booking.age')}
                          min="0"
                          max="120"
                          value={guest.age || ''}
                          onChange={(e) => {
                            const newGuests = contactData.guestList ? JSON.parse(contactData.guestList) : [];
                            if (!newGuests[index]) newGuests[index] = {};
                            newGuests[index].age = e.target.value;
                            setContactData(prev => ({ ...prev, guestList: JSON.stringify(newGuests) }));
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="label">{t('booking.message')}</label>
                <textarea
                  className="input-clean h-24"
                  value={contactData.message}
                  onChange={(e) => setContactData(prev => ({ ...prev, message: e.target.value }))}
                  placeholder={t('booking.message')}
                  title={t('booking.message')}
                />
              </div>

              <div className="bg-warm-cream p-4 rounded-architectural border border-stone/20">
                <div className="flex justify-between mb-2">
                  <span className="font-semibold">{t('booking.dates')}:</span>
                  <span className="font-bold">{bookingData.checkIn} → {bookingData.checkOut}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="font-semibold">{t('booking.guests')}:</span>
                  <span className="font-bold">{bookingData.guests}</span>
                </div>
                <div className="flex justify-between font-bold border-t border-stone/30 pt-2 mt-2 text-lg">
                  <span>{t('booking.total')}:</span>
                  <span className="text-terracotta">€{availabilityResult?.grandTotal}</span>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-architectural p-4">
                <p className="text-sm text-yellow-800 font-semibold text-center">
                  {t('booking.youWontBeCharged')}
                </p>
              </div>

              <button
                type="submit"
                className="btn-book w-full"
              >
                {t('booking.sendRequest')}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {showImageModal && (
        <div
          className="modal-overlay fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={closeImageModal}
        >
          <div
            className="modal-content max-w-7xl w-full bg-white p-4 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeImageModal}
              className="absolute top-4 right-4 text-4xl text-gray-600 hover:text-terracotta transition-colors z-10 bg-white/80 w-12 h-12 flex items-center justify-center"
            >
              ×
            </button>

            <div className="relative mb-4">
              <img
                src={mockProperty.images[selectedImage]}
                alt={`Property ${selectedImage + 1}`}
                className="w-full h-auto max-h-[70vh] object-contain"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = '/images/pool/main.jpg';
                }}
              />

              {selectedImage > 0 && (
                <button
                  onClick={() => setSelectedImage(prev => prev - 1)}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 text-3xl w-12 h-12 flex items-center justify-center transition-all hover:scale-110"
                >
                  ‹
                </button>
              )}

              {selectedImage < mockProperty.images.length - 1 && (
                <button
                  onClick={() => setSelectedImage(prev => prev + 1)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 text-3xl w-12 h-12 flex items-center justify-center transition-all hover:scale-110"
                >
                  ›
                </button>
              )}
            </div>

            <div className="text-center text-gray-600 mb-4">
              {selectedImage + 1} / {mockProperty.images.length}
            </div>

            {/* Thumbnail Navigation */}
            <div className="flex gap-2 overflow-x-auto pb-2 px-2 max-h-32">
              {mockProperty.images.map((img, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`flex-shrink-0 w-20 h-20 overflow-hidden transition-all ${index === selectedImage
                    ? 'ring-4 ring-terracotta opacity-100'
                    : 'opacity-60 hover:opacity-100'
                    }`}
                >
                  <img
                    src={img}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/images/pool/main.jpg';
                    }}
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;
